import { motion } from "framer-motion";
import { useLang } from "../i18n";

const CHAPTERS = [
  {
    n: "01",
    title: { en: "Lost-Wax Metal Casting", it: "Fusione a Cera Persa" },
    img: "https://images.unsplash.com/photo-1609619742069-f5e18afeef17?q=80&w=1000&auto=format&fit=crop",
    desc: {
      en: "Your wax model disappears into plaster; molten gold, silver, platinum or palladium takes its place. Vacuum-assisted casting with diligent multi-stage quality control, using 100% recycled alloys on request.",
      it: "Il tuo modello in cera sparisce nel gesso; oro, argento, platino o palladio fusi ne prendono il posto. Fusione assistita da vuoto con controllo qualità in più fasi, leghe 100% riciclate su richiesta.",
    },
    specs: [
      { en: "Au · Ag · Pt · Pd alloys", it: "Leghe Au · Ag · Pt · Pd" },
      { en: "Vacuum-assisted pour", it: "Colata assistita da vuoto" },
      { en: "Multi-stage QC", it: "Controllo qualità multi-fase" },
    ],
    turn: { en: "3–5 working days", it: "3–5 giorni lavorativi" },
  },
  {
    n: "02",
    title: { en: "Gemstone & Diamond Setting", it: "Incastonatura di Pietre e Diamanti" },
    img: "https://images.unsplash.com/photo-1611955167811-4711904bb9f8?q=80&w=1000&auto=format&fit=crop",
    desc: {
      en: "Prong, bezel, pavé or channel — stones are seated under the microscope by hands that have set thousands. Every seat checked for security and symmetry before it leaves the bench.",
      it: "Griffe, castone, pavé o binario — le pietre vengono incassate al microscopio da mani che ne hanno incastonate migliaia. Ogni sede controllata per sicurezza e simmetria prima di lasciare il banco.",
    },
    specs: [
      { en: "Prong · Bezel · Pavé · Channel", it: "Griffe · Castone · Pavé · Binario" },
      { en: "Microscope setting", it: "Incastonatura al microscopio" },
      { en: "Tension & flush work", it: "Tensione e incasso a filo" },
    ],
    turn: { en: "2–4 working days", it: "2–4 giorni lavorativi" },
  },
  {
    n: "03",
    title: { en: "Hand & Laser Engraving", it: "Incisione a Mano e Laser" },
    img: "https://images.unsplash.com/photo-1610694955371-d4a3e0ce4b52?q=80&w=1000&auto=format&fit=crop",
    desc: {
      en: "From a whispered date inside a band to a full family crest. Choose the burin's hand-cut warmth or fibre-laser precision on flat and curved surfaces alike.",
      it: "Da una data sussurrata dentro una fede a uno stemma di famiglia completo. Scegli il calore dell'incisione a bulino o la precisione del laser a fibra, su superfici piane e curve.",
    },
    specs: [
      { en: "Hand-cut burin work", it: "Incisione a bulino" },
      { en: "Fibre laser, 100×100 mm", it: "Laser a fibra, 100×100 mm" },
      { en: "Crests, monograms, dates", it: "Stemmi, monogrammi, date" },
    ],
    turn: { en: "1–3 working days", it: "1–3 giorni lavorativi" },
  },
  {
    n: "04",
    title: { en: "Artisan Wax Carving", it: "Scultura Artigianale in Cera" },
    img: "https://images.unsplash.com/photo-1599071338288-49173359e0fd?q=80&w=1000&auto=format&fit=crop",
    desc: {
      en: "The oldest way to dream in three dimensions. A block of jeweller's wax, carved by hand into your design — organic textures and one-of-one forms that no machine can imitate.",
      it: "Il modo più antico di sognare in tre dimensioni. Un blocco di cera da orafo, scolpito a mano nel tuo design — texture organiche e forme uniche che nessuna macchina può imitare.",
    },
    specs: [
      { en: "One-of-one masters", it: "Modelli pezzo unico" },
      { en: "Organic texture work", it: "Texture organiche" },
      { en: "Castable carving wax", it: "Cera fondibile da scultura" },
    ],
    turn: { en: "4–7 working days", it: "4–7 giorni lavorativi" },
  },
  {
    n: "05",
    title: { en: "CAD Modelling & 3D Prototype", it: "Progettazione CAD e Prototipo 3D" },
    img: "https://images.unsplash.com/photo-1620656798579-1984d9e87df7?q=80&w=1000&auto=format&fit=crop",
    desc: {
      en: "Your sketch becomes a manufacture-ready CAD model with spec sheets and photoreal renders, then a 16-micron wax prototype you can hold, try and approve before a single gram is cast.",
      it: "Il tuo bozzetto diventa un modello CAD pronto per la produzione, con schede tecniche e render fotorealistici, poi un prototipo in cera da 16 micron da toccare e approvare prima di fondere un solo grammo.",
    },
    specs: [
      { en: "Manufacture-ready 3DM/STL", it: "File 3DM/STL pronti per la produzione" },
      { en: "Photoreal renders included", it: "Render fotorealistici inclusi" },
      { en: "16 µm wax printing", it: "Stampa in cera 16 µm" },
    ],
    turn: { en: "2–4 working days", it: "2–4 giorni lavorativi" },
  },
];

export default function Services() {
  const { L } = useLang();
  return (
    <section id="services" data-testid="services-manifesto-section" className="relative mx-auto max-w-7xl px-6 py-28 lg:py-40">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-24 max-w-2xl"
      >
        <p className="mono-tech mb-5 text-[#A8853B]">{L({ en: "The Craft — Five Chapters", it: "Il Mestiere — Cinque Capitoli" })}</p>
        <h2 className="font-display text-4xl leading-tight tracking-tight text-[#0E3B33] sm:text-5xl">
          {L({ en: "Ancient fire,", it: "Fuoco antico," })} <span className="gold-text italic">{L({ en: "modern precision.", it: "precisione moderna." })}</span>
        </h2>
        <p className="mt-6 text-base leading-relaxed text-[#4A5248] sm:text-lg">
          {L({
            en: "Every service under one roof, from first sketch to hallmark. No hand-offs, no black boxes — you watch your piece move through the atelier.",
            it: "Ogni servizio sotto lo stesso tetto, dal primo bozzetto al punzone. Nessun passaggio di mano, nessuna scatola nera — segui il tuo pezzo attraverso l'atelier.",
          })}
        </p>
      </motion.div>

      <div className="space-y-28 lg:space-y-40">
        {CHAPTERS.map((c, i) => (
          <motion.article
            key={c.n}
            data-testid={`service-chapter-${c.n}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="grid items-center gap-10 lg:grid-cols-12"
          >
            <div className={`relative lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
              <div className="overflow-hidden rounded-sm border border-[#C9A84C]/30 shadow-lg" style={{ clipPath: i % 2 ? "polygon(0 0, 100% 0, 100% 100%, 12% 100%, 0 88%)" : "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}>
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  src={c.img}
                  alt={L(c.title)}
                  loading="lazy"
                  className="h-[380px] w-full object-cover"
                  data-testid={`service-image-${c.n}`}
                />
              </div>
              <div className={`absolute top-6 font-display text-8xl leading-none text-[#C9A84C]/25 ${i % 2 ? "-right-2" : "-left-2"}`}>{c.n}</div>
            </div>
            <div className={`lg:col-span-6 ${i % 2 ? "lg:order-1 lg:pr-8" : "lg:col-start-7"}`}>
              <div className="mono-tech mb-4 flex items-center gap-4 text-[#8A8F82]">
                <span className="text-[#A8853B]">{L({ en: "Chapter", it: "Capitolo" })} {c.n}</span>
                <span className="h-px w-10 bg-[#C9A84C]/50" />
                <span>{L({ en: "Turnaround", it: "Tempi" })} {L(c.turn)}</span>
              </div>
              <h3 className="font-display text-3xl tracking-tight text-[#0E3B33] sm:text-4xl">{L(c.title)}</h3>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-[#4A5248]">{L(c.desc)}</p>
              <ul className="mt-7 space-y-2.5">
                {c.specs.map((s, j) => (
                  <li key={j} className="flex items-center gap-3 text-sm text-[#1E2B26]" data-testid={`service-spec-${c.n}-${j}`}>
                    <span className="h-1 w-1 rounded-full bg-[#C9A84C]" />
                    {L(s)}
                  </li>
                ))}
              </ul>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
